import React, { useState, useEffect, useRef } from 'react';
import BackgroundIcons from '../../components/BackgroundIcons';
import NotificationModal from '../../components/NotificationModal';
import { generateThankYouPDF } from '../../utils/pdfGenerator';

const ThankYouNoteGenerator = () => {
  const [formData, setFormData] = useState({
    coupleNames: '',
    tone: 'warm',
    customMessage: ''
  });
  const [isVisible, setIsVisible] = useState(false);
  const [previewNote, setPreviewNote] = useState('');
  const [weddingImage, setWeddingImage] = useState(null);
  const previewCardRef = useRef(null);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
  };

  const closeNotification = () => {
    setNotification({ show: false, type: '', message: '' });
  };

  const toneOptions = [
    { value: 'warm', label: 'Warm & Personal', icon: '💖', description: 'Heartfelt and intimate' },
    { value: 'elegant', label: 'Elegant & Formal', icon: '✨', description: 'Sophisticated and refined' },
    { value: 'casual', label: 'Casual & Friendly', icon: '😊', description: 'Relaxed and approachable' },
    { value: 'grateful', label: 'Deeply Grateful', icon: '🙏', description: 'Emphasizes appreciation' },
    { value: 'custom', label: 'Custom Message', icon: '✍️', description: 'Write your own message' }
  ];

  useEffect(() => {
    setIsVisible(true);
    generatePreview();
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 10 * 1024 * 1024) {
        showNotification('error', 'Image size must be less than 10MB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setWeddingImage(e.target.result);
        showNotification('success', 'Wedding photo uploaded successfully!');
      };
      reader.readAsDataURL(file);
    } else if (file) {
      showNotification('error', 'Please upload a valid image file (JPG, PNG, etc.).');
    }
  };

  const generatePreview = () => {
    if (!formData.coupleNames) return;

    const templates = {
      warm: `From the bottom of our hearts, thank you for being part of our special day. Your presence meant the world to us.

Your thoughtful gifts were absolutely perfect. We're so grateful for your love and support.

With all our love`,

      elegant: `We are deeply honored that you joined us in celebrating our wedding day. Your presence added such joy to this momentous occasion.

Thank you for your generous gifts and continued friendship as we begin this new chapter together.

With sincere gratitude`,

      casual: `Thank you so much for celebrating with us! It wouldn't have been the same without you there.

We absolutely love the gifts - they're perfect for our new place. Can't wait to see you again soon!

Love and hugs`,

      grateful: `Our hearts are overflowing with gratitude. Having you there to witness our union was truly one of the greatest gifts we could have received.

Thank you for your generous gifts and for being such an important part of our lives.

With endless appreciation`,

      custom: `${formData.customMessage || 'Write your custom message here...'}`
    };

    setPreviewNote(templates[formData.tone] || templates.warm);
  };

  const downloadNote = () => {
    if (!formData.coupleNames || !formData.coupleNames.trim()) {
      showNotification('error', 'Please enter couple names to generate the thank you note.');
      return;
    }

    if (!weddingImage) {
      showNotification('error', 'Please upload a wedding photo to complete your thank you note.');
      return;
    }

    if (!previewNote) {
      showNotification('error', 'Please wait for the preview to generate.');
      return;
    }

    generateThankYouPDF(previewNote, formData.coupleNames, weddingImage);
    showNotification('success', 'Opening print dialog... Save as PDF from the print options.');
  };

  const clearForm = () => {
    setFormData({
      coupleNames: '',
      tone: 'warm',
      customMessage: ''
    });
    setPreviewNote('');
    setWeddingImage(null);
  };  const pageIcons = [
    { emoji: '💕', size: '4xl', opacity: '15' },
    { emoji: '💖', size: '3xl', opacity: '15' },
    { emoji: '💕', size: '6xl', opacity: '15' },
    { emoji: '💖', size: '4xl', opacity: '15' }
  ];



  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={pageIcons} />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Shimmer animation for memories */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-15"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 4}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          >
            <span className="text-gold-300 text-lg">✨</span>
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        
        {/* Elegant Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <div className="relative">
              <span className="text-2xl">💌</span>
              <span className="absolute -top-1 -right-1 text-lg animate-pulse">💖</span>
            </div>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Thank You Note Generator
          </h1>
          
          {/* Soft heart motif underline */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400">💕</div>
            </div>
          </div>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Express your heartfelt gratitude with personalized thank you notes that capture the joy and love of your special day
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Form Section */}
          <div className="lg:col-span-1 space-y-8">
            
            {/* Basic Information */}
            <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8">
                <div className="flex items-center mb-8">
                  <div className="w-12 h-12 bg-gradient-to-r from-rose-400 to-pink-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                    <span className="text-2xl">💕</span>
                  </div>
                  <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                    Wedding Details
                  </h3>
                </div>
                
                <div className="space-y-6">
                  {/* Couple Names */}
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-rose-400">
                      <span className="text-xl">💑</span>
                    </div>
                    <input
                      type="text"
                      value={formData.coupleNames}
                      onChange={(e) => handleInputChange('coupleNames', e.target.value)}
                      placeholder="Your names (e.g., Sarah & Michael)"
                      className="w-full pl-12 pr-4 py-4 border-2 border-gold-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium"
                    />
                  </div>

                  {/* Wedding Image Upload */}
                  <div className="relative">
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      Wedding Photo
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="wedding-image-upload"
                      />
                      <label
                        htmlFor="wedding-image-upload"
                        className="flex items-center justify-center w-full p-6 border-2 border-dashed border-gold-300 rounded-xl cursor-pointer hover:border-gold-500 hover:bg-gold-50/50 transition-all duration-300"
                      >
                        {weddingImage ? (
                          <div className="text-center">
                            <div className="w-20 h-20 mx-auto mb-3 rounded-lg overflow-hidden">
                              <img src={weddingImage} alt="Wedding" className="w-full h-full object-cover" />
                            </div>
                            <p className="text-sm text-gray-600">✓ Photo uploaded - Click to change</p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <span className="text-4xl mb-2 block">📷</span>
                            <p className="text-sm font-medium text-gray-700">Click to upload wedding photo</p>
                            <p className="text-xs text-gray-500 mt-1">HD images supported</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Tone Selection */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-4">Tone of Voice</label>
                    <div className="space-y-3">
                      {toneOptions.map((option) => (
                        <label
                          key={option.value}
                          className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                            formData.tone === option.value
                              ? 'border-gold-400 bg-gradient-to-r from-gold-50 to-yellow-50 shadow-lg'
                              : 'border-gray-200 bg-white/60 hover:border-gold-300 hover:shadow-md'
                          }`}
                        >
                          <input
                            type="radio"
                            name="tone"
                            value={option.value}
                            checked={formData.tone === option.value}
                            onChange={(e) => handleInputChange('tone', e.target.value)}
                            className="sr-only"
                          />
                          <span className="text-2xl mr-3">{option.icon}</span>
                          <div>
                            <div className="font-semibold text-gray-800">{option.label}</div>
                            <div className="text-sm text-gray-600">{option.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Custom Message (only shown when custom tone selected) */}
                  {formData.tone === 'custom' && (
                    <div className="relative">
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Your Custom Message
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-4 text-pink-400">
                          <span className="text-xl">✍️</span>
                        </div>
                        <textarea
                          value={formData.customMessage}
                          onChange={(e) => handleInputChange('customMessage', e.target.value)}
                          placeholder="Write your heartfelt message here... (The signature will be added automatically)"
                          rows="6"
                          className="w-full pl-12 pr-4 py-4 border-2 border-gold-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium resize-none"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Preview & Download Section */}
          <div className="lg:col-span-2">
            <div className={`transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8 sticky top-6">
                <div className="flex items-center mb-8">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                    <span className="text-2xl">👁️</span>
                  </div>
                  <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    Preview & Download
                  </h3>
                </div>

                {/* Preview Note */}
                <div className="mb-8">
                  {/* Split-View Card: Image + Text */}
                  <div ref={previewCardRef} className="bg-white rounded-2xl overflow-hidden shadow-2xl border-4 border-gold-300" style={{ width: '721px', height: '508px', margin: '0 auto' }}>
                    <div className="flex flex-col md:flex-row h-full">
                      
                      {/* Left: Image Section */}
                      <div className="md:w-1/2 relative overflow-hidden bg-gradient-to-br from-rose-100 to-pink-100">
                        {weddingImage ? (
                          <img 
                            src={weddingImage} 
                            alt="Wedding" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="text-center p-8">
                              <span className="text-6xl mb-4 block opacity-30">📷</span>
                              <p className="text-gray-400 font-medium">Upload a wedding photo</p>
                              <p className="text-gray-300 text-sm mt-2">Your image will appear here</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right: Text Section */}
                      <div className="md:w-1/2 bg-gradient-to-br from-rose-50/80 to-pink-50/80 p-6 flex flex-col justify-center text-center overflow-y-auto">
                        <div className="relative">
                          <h2 className="font-serif font-bold bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 bg-clip-text text-transparent mb-4" style={{ fontFamily: "'Great Vibes', 'Allura', 'Dancing Script', cursive", fontSize: '2.5rem', letterSpacing: '1px' }}>
                            Thank You
                          </h2>
                          <pre className="text-gray-800 whitespace-pre-wrap break-words text-center mb-4" style={{ fontFamily: "'Allura', 'Great Vibes', cursive", fontSize: '1.125rem', lineHeight: '1.8', fontWeight: '400' }}>
                            {previewNote || 'Enter your names to see a preview...'}
                          </pre>
                          {formData.coupleNames && (
                            <div className="text-gray-800" style={{ fontFamily: "'Allura', 'Great Vibes', cursive", fontSize: '2rem', background: 'linear-gradient(135deg, #ec4899 0%, #d4af37 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                              {formData.coupleNames}
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-8">
                  <button
                    onClick={downloadNote}
                    disabled={!previewNote || !formData.coupleNames || !weddingImage}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-4 px-6 rounded-xl font-bold hover:from-purple-600 hover:to-pink-600 hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none animate-pulse"
                  >
                    <span className="mr-2">🖨️</span>
                    Download as PDF
                  </button>
                </div>

                <div className="mt-6 text-center">
                  <button
                    onClick={clearForm}
                    className="px-8 py-3 border-2 border-gray-300 text-gray-600 rounded-xl font-bold hover:bg-gray-50 hover:shadow-lg transition-all duration-300"
                  >
                    🔄 Start Over
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Luxury Footer */}
        <div className={`mt-16 text-center transition-all duration-700 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-gold-400 to-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-light italic">
            Treasuring every memory of your VowAI wedding journey
          </p>
        </div>
      </div>

      {/* Notification Modal */}
      <NotificationModal
        show={notification.show}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        message={notification.message}
        onClose={closeNotification}
      />
    </div>
  );
};

export default ThankYouNoteGenerator;
